import { useState, useEffect } from 'react'
import { FormConfiguration, FormAnswers, FormState } from '../../types/formConfig'
import { saveFormState, loadFormState, clearFormState } from '../../utils/formStorage'
import { getVisibleQuestions } from '../../utils/conditionalLogic'
import { validateAnswer } from '../../utils/validation'
import ProgressIndicator from './ProgressIndicator'
import NavigationControls from './NavigationControls'
import QuestionRenderer from './QuestionRenderer'
import FormSummary from './FormSummary'
import { useUserContext } from '@/context/UserContext'

interface TypeFormFlowProps {
  config: FormConfiguration
  onComplete?: (answers: FormAnswers) => void | Promise<void>
  onPartialSubmit?: (answers: FormAnswers, questionId: string | null) => Promise<void>
  className?: string
}

const TypeFormFlow = ({
  config,
  onComplete,
  onPartialSubmit,
  className = ''
}: TypeFormFlowProps) => {
  const {
    answers: rawAnswers,
    setAnswers,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    validationError,
    setValidationError,
  } = useUserContext()
  const answers = rawAnswers ?? {}
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [returnToSummary, setReturnToSummary] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const savedState = loadFormState(config.formId)
    if (savedState) {
      setAnswers(savedState.answers)
      const visibleAtLoad = getVisibleQuestions(config.questions, savedState.answers)
      const safeIndex = Math.min(
        Math.max(0, savedState.currentQuestionIndex),
        Math.max(0, visibleAtLoad.length - 1)
      )
      setCurrentQuestionIndex(safeIndex)
      if (savedState.showSummary) {
        setShowSummary(true)
      }
    }
    setIsLoading(false)
  }, [config.formId])

  useEffect(() => {
    if (isLoading) return
    const state: Omit<FormState, 'formId'> = {
      timestamp: new Date().toISOString(),
      answers,
      currentQuestionIndex,
      partiallySubmitted: false,
      completedQuestionIds: Object.keys(answers),
      showSummary
    }
    saveFormState(config.formId, state)
  }, [answers, currentQuestionIndex, showSummary, config.formId, isLoading])

  const visibleQuestions = getVisibleQuestions(config.questions, answers)
  const currentQuestion = visibleQuestions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === visibleQuestions.length - 1
  const isFirstQuestion = currentQuestionIndex === 0
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined

  const handleAnswerChange = (value: string | string[] | number | object) => {
    if (!currentQuestion) return
    setAnswers({ ...answers, [currentQuestion.id]: value })
    setValidationError(null)
  }

  const validateCurrentQuestion = (): boolean => {
    if (!currentQuestion) return true
    const error = validateAnswer(currentQuestion, currentAnswer)
    if (error) {
      setValidationError(error)
      return false
    }
    setValidationError(null)
    return true
  }

  const handleNext = async () => {
    if (!validateCurrentQuestion()) return

    if (currentQuestion?.partialSubmit && onPartialSubmit) {
      setIsSubmitting(true)
      try {
        await onPartialSubmit(answers, currentQuestion.id)
      } catch (error) {
        console.error('Partial submit error:', error)
        setValidationError('Failed to save progress. Please try again.')
        setIsSubmitting(false)
        return
      }
      setIsSubmitting(false)
    }

    if (returnToSummary) {
      setShowSummary(true)
      setReturnToSummary(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    if (isLastQuestion) {
      setShowSummary(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handleBack = () => {
    if (showSummary) {
      setShowSummary(false)
      setReturnToSummary(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (!isFirstQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
      setValidationError(null)
    }
  }

  const handleEdit = (questionId: string) => {
    const questionIndex = visibleQuestions.findIndex(q => q.id === questionId)
    if (questionIndex !== -1) {
      setCurrentQuestionIndex(questionIndex)
      setShowSummary(false)
      setReturnToSummary(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleSubmitFromSummary = async () => {
    if (!turnstileToken) {
      setValidationError('Please complete the security verification.')
      return
    }
    setIsSubmitting(true)
    try {
      if (onComplete) {
        await onComplete({ ...answers, turnstileToken })
      }
      clearFormState(config.formId)
    } catch (error) {
      console.error('Form submission error:', error)
      setValidationError('Failed to submit form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showSummary) return
      if (e.key === 'Enter' && !validationError) {
        if (currentQuestion?.questionType === 'radio' || currentQuestion?.questionType === 'checkbox') {
          if (currentAnswer) handleNext()
        }
      }
    }
    window.addEventListener('keypress', handleKeyPress)
    return () => window.removeEventListener('keypress', handleKeyPress)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleNext is stable, adding it causes re-registration on every render
  }, [currentQuestion, currentAnswer, validationError, showSummary])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-green"></div>
      </div>
    )
  }

  if (showSummary) {
    return (
      <div className={`w-full ${className}`}>
        <FormSummary
          questions={visibleQuestions}
          answers={answers}
          onEdit={handleEdit}
          onSubmit={handleSubmitFromSummary}
          isSubmitting={isSubmitting}
          turnstileToken={turnstileToken}
          onTurnstileSuccess={(token) => setTurnstileToken(token)}
          onTurnstileError={() => setTurnstileToken('')}
          onTurnstileExpire={() => setTurnstileToken('')}
        />
      </div>
    )
  }

  if (!currentQuestion) {
    // Index is out of bounds (stale saved state or config change) — reset silently
    setCurrentQuestionIndex(0)
    return null
  }

  return (
    <div className={`w-full ${className}`}>
      <ProgressIndicator
        currentStep={currentQuestionIndex + 1}
        totalSteps={visibleQuestions.length}
        className="mb-8"
      />
      <div className="min-h-[400px] mb-8">
        <QuestionRenderer
          question={currentQuestion}
          value={currentAnswer ?? ''}
          onChange={handleAnswerChange}
          error={validationError || undefined}
          allAnswers={answers}
        />
      </div>
      <NavigationControls
        onNext={handleNext}
        onBack={handleBack}
        onSubmit={undefined}
        showBack={!isFirstQuestion || returnToSummary}
        showNext={true}
        showSubmit={false}
        isNextDisabled={!currentAnswer && currentQuestion.required}
        isSubmitting={isSubmitting}
        nextLabel={isLastQuestion ? 'Review Answers' : (returnToSummary ? 'Back to Summary' : 'Next')}
        backLabel={returnToSummary ? 'Previous' : 'Back'}
        className="mt-8"
      />
      {isFirstQuestion && config.description && !returnToSummary && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{config.description}</p>
        </div>
      )}
    </div>
  )
}

export default TypeFormFlow
